import { useState, useRef, type FormEvent } from 'react';

export default function NewsletterForm() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const formEl = formRef.current;
    if (!formEl) return;

    const formData = new FormData(formEl);
    const honeypot = formData.get('phone_alt')?.toString().trim() || '';

    if (honeypot) {
      setStatus('success');
      setEmail('');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rdgjsnlsngrm4lmam0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email }).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      const code = (parsed as { code?: string })?.code;
      const serverMsg =
        (parsed as { meta?: { message?: string } })?.meta?.message ||
        (parsed as { message?: string })?.message ||
        '';

      if (response.ok && code === 'OK') {
        setStatus('success');
        setEmail('');
      } else if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
        setStatus('error');
        setErrorMsg('提交失败，请稍后再试。');
      } else {
        setStatus('error');
        setErrorMsg(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络错误，请检查连接后重试。');
    }
  };

  return (
    <section className="w-full py-14 md:py-20 bg-background-100">
      <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
        <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-3">
          订阅久久精品
        </h2>
        <p className="text-sm md:text-base text-foreground-600 mb-8 max-w-lg mx-auto">
          第一时间获取精选好物推荐、独家优惠和品质生活灵感。
        </p>

        <form
          ref={formRef}
          onSubmit={handleSubmit}
          data-readdy-form
          className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
        >
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="请输入您的邮箱地址"
            required
            className="flex-1 px-4 py-3 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-shadow"
          />
          <input
            type="text"
            name="phone_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="honeypot-field"
          />
          <button
            type="submit"
            disabled={status === 'loading'}
            className="px-6 py-3 text-sm font-medium whitespace-nowrap rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60"
          >
            {status === 'loading' ? '提交中...' : '立即订阅'}
          </button>
        </form>

        {status === 'success' && (
          <p className="mt-4 text-sm text-green-600 font-medium">感谢订阅！我们会将最新精选好物发送到您的邮箱。</p>
        )}
        {status === 'error' && (
          <p className="mt-4 text-sm text-red-500 font-medium">{errorMsg}</p>
        )}

        <p className="mt-4 text-xs text-foreground-400">我们尊重您的隐私，绝不发送垃圾邮件。</p>
      </div>
    </section>
  );
}